/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import type { User } from 'firebase/auth';
import { UserRole, getInitialRoleFromEmail, getPermissions, type RolePermissions } from '@/lib/roles';

interface UserProfile {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  role: UserRole;
  isAdmin: boolean; // Deprecated - use role >= UserRole.ADMIN
  createdAt: any;
  lastLogin: any;
  subscriptionStatus?: 'free' | 'pro' | 'enterprise';
  organizationId?: string;
}

interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  permissions: RolePermissions;
  signInWithGoogle: () => Promise<void>;
  signInWithEmailPassword: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshUserProfile: () => Promise<void>;
  hasPermission: (permission: keyof RolePermissions) => boolean;
  hasMinimumRole: (minimumRole: UserRole) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const IMMEDIATE_AUTH_PATHS = [
  /^\/(dashboard|community|account|data-admin|county-verification)(\/|$)/,
  /\/support(\/|$)/,
];

export function shouldStartAuthImmediately(pathname: string): boolean {
  return IMMEDIATE_AUTH_PATHS.some((pattern) => pattern.test(pathname));
}

function scheduleAuthBootstrap(callback: () => void, immediate: boolean): () => void {
  if (immediate) {
    const timer = window.setTimeout(callback, 0);
    return () => window.clearTimeout(timer);
  }

  const requestIdle = (window as Window & { requestIdleCallback?: typeof requestIdleCallback }).requestIdleCallback;
  const cancelIdle = (window as Window & { cancelIdleCallback?: typeof cancelIdleCallback }).cancelIdleCallback;

  if (requestIdle) {
    const idleHandle = requestIdle(callback, { timeout: 2500 });
    return () => cancelIdle?.(idleHandle);
  }

  const timer = window.setTimeout(callback, 1800);
  return () => window.clearTimeout(timer);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  // Get permissions based on current user role
  const permissions = userProfile ? getPermissions(userProfile.role) : getPermissions(UserRole.GUEST);

  useEffect(() => {
    let unsubscribe: (() => void) | null = null;
    let disposed = false;
    const cancelAuthBootstrap = scheduleAuthBootstrap(() => {
      void (async () => {
        try {
          const [{ auth, db }, authSdk, firestoreSdk] = await Promise.all([
            import('@/lib/firebase'),
            import('firebase/auth'),
            import('firebase/firestore'),
          ]);
          if (disposed) return;

          unsubscribe = authSdk.onAuthStateChanged(auth, async (firebaseUser) => {
            setUser(firebaseUser);

            if (firebaseUser) {
              // Load or create user profile
              const userRef = firestoreSdk.doc(db, 'users', firebaseUser.uid);
              const userSnap = await firestoreSdk.getDoc(userRef);

              if (userSnap.exists()) {
                const data = userSnap.data();

                // Migrate old isAdmin field to role system
                let role = data.role as UserRole;
                if (role === undefined || role === null) {
                  if (data.isAdmin === true) {
                    role = UserRole.ADMIN;
                  } else {
                    role = getInitialRoleFromEmail(firebaseUser.email || '');
                  }
                }

                await firestoreSdk.setDoc(
                  userRef,
                  {
                    lastLogin: firestoreSdk.serverTimestamp(),
                    role,
                  },
                  { merge: true },
                );

                const profile: UserProfile = {
                  uid: firebaseUser.uid,
                  email: firebaseUser.email,
                  displayName: firebaseUser.displayName,
                  photoURL: firebaseUser.photoURL,
                  role,
                  isAdmin: role >= UserRole.ADMIN,
                  createdAt: data.createdAt,
                  lastLogin: firestoreSdk.serverTimestamp(),
                  subscriptionStatus: data.subscriptionStatus || 'free',
                  organizationId: data.organizationId,
                };

                setUserProfile(profile);
              } else {
                const initialRole = getInitialRoleFromEmail(firebaseUser.email || '');

                const newProfile: UserProfile = {
                  uid: firebaseUser.uid,
                  email: firebaseUser.email,
                  displayName: firebaseUser.displayName,
                  photoURL: firebaseUser.photoURL,
                  role: initialRole,
                  isAdmin: initialRole >= UserRole.ADMIN,
                  createdAt: firestoreSdk.serverTimestamp(),
                  lastLogin: firestoreSdk.serverTimestamp(),
                  subscriptionStatus: 'free',
                };

                await firestoreSdk.setDoc(userRef, newProfile);
                setUserProfile(newProfile);
              }
            } else {
              setUserProfile(null);
            }

            setLoading(false);
          });
        } catch (error) {
          console.error('[Auth] Deferred Firebase initialization failed:', error);
          setLoading(false);
        }
      })();
    }, shouldStartAuthImmediately(window.location.pathname));

    return () => {
      disposed = true;
      cancelAuthBootstrap();
      unsubscribe?.();
    };
  }, []);

  const signInWithGoogle = async () => {
    const [{ auth }, authSdk] = await Promise.all([import('@/lib/firebase'), import('firebase/auth')]);
    const provider = new authSdk.GoogleAuthProvider();
    provider.setCustomParameters({
      prompt: 'select_account',
    });

    try {
      await authSdk.signInWithPopup(auth, provider);
    } catch (error) {
      const errorCode =
        typeof error === 'object' && error !== null && 'code' in error
          ? String((error as { code?: unknown }).code)
          : '';

      if (
        errorCode === 'auth/popup-blocked' ||
        errorCode === 'auth/web-storage-unsupported' ||
        errorCode === 'auth/operation-not-supported-in-this-environment'
      ) {
        await authSdk.signInWithRedirect(auth, provider);
        return;
      }

      console.error('Error signing in with Google:', error);
      throw error;
    }
  };

  const signInWithEmailPassword = async (email: string, password: string) => {
    if (import.meta.env.VITE_USE_FIREBASE_EMULATORS !== 'true') {
      throw new Error('Local email sign-in is only available when Firebase emulators are enabled.');
    }
    const [{ auth }, authSdk] = await Promise.all([import('@/lib/firebase'), import('firebase/auth')]);
    await authSdk.signInWithEmailAndPassword(auth, email, password);
  };

  const signOut = async () => {
    try {
      const [{ auth }, authSdk] = await Promise.all([import('@/lib/firebase'), import('firebase/auth')]);
      await authSdk.signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  const refreshUserProfile = async () => {
    if (!user) return;
    const [{ db }, firestoreSdk] = await Promise.all([import('@/lib/firebase'), import('firebase/firestore')]);
    const userSnapshot = await firestoreSdk.getDoc(firestoreSdk.doc(db, 'users', user.uid));
    if (!userSnapshot.exists()) return;
    const data = userSnapshot.data();
    let role = data.role as UserRole;
    if (role === undefined || role === null) {
      role = data.isAdmin === true ? UserRole.ADMIN : getInitialRoleFromEmail(user.email || '');
    }
    setUserProfile({
      uid: user.uid,
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL,
      role,
      isAdmin: role >= UserRole.ADMIN,
      createdAt: data.createdAt,
      lastLogin: data.lastLogin,
      subscriptionStatus: data.subscriptionStatus || 'free',
      organizationId: data.organizationId,
    });
  };

  const hasPermission = (permission: keyof RolePermissions): boolean => {
    return permissions[permission];
  };

  const hasMinimumRole = (minimumRole: UserRole): boolean => {
    const currentRole = userProfile?.role ?? UserRole.GUEST;
    return currentRole >= minimumRole;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        userProfile,
        loading,
        permissions,
        signInWithGoogle,
        signInWithEmailPassword,
        signOut,
        refreshUserProfile,
        hasPermission,
        hasMinimumRole,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
